import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";

import {
    fetchOrganizationDevices,
    fetchPolicyGroups,
    shareAddressBook,
    getAddressBookShares,
} from "js/Api/index";

// ─── helpers ───────────────────────────────────────────────────────────────

function initialsColor(name) {
    const hue = ((name || "X").charCodeAt(0) * 13) % 360;
    return `hsl(${hue},50%,52%)`;
}
function initials(name) {
    return (name || "?").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

// ─── selectable row ─────────────────────────────────────────────────────────

function SelectRow({ title, subtitle, imageSrc, checked, onToggle }) {
    const [hovered, setHovered] = useState(false);
    const [imgOk, setImgOk] = useState(true);
    const bg = checked ? "#eef3ff" : hovered ? "#f7f9ff" : "#fff";
    const border = checked ? "#93b4f5" : hovered ? "#d0dff7" : "#f0f0f0";
    return (
        <div
            onClick={onToggle}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 14px", background: bg,
                border: `1px solid ${border}`, borderRadius: 10,
                cursor: "pointer", transition: "all 0.12s", userSelect: "none",
            }}
        >
            <div style={{
                width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                border: `2px solid ${checked ? "#1976d2" : "#ccc"}`,
                background: checked ? "#1976d2" : "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.12s",
            }}>
                {checked && <CheckIcon sx={{ fontSize: "0.85rem", color: "#fff" }} />}
            </div>
            {imageSrc && imgOk ? (
                <img
                    src={imageSrc}
                    alt={title || ""}
                    onError={() => setImgOk(false)}
                    style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                />
            ) : (
                <div style={{
                    width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                    background: initialsColor(title), color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 700,
                }}>
                    {initials(title)}
                </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {title || "—"}
                </div>
                {subtitle && (
                    <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{subtitle}</div>
                )}
            </div>
        </div>
    );
}

// ─── main modal ─────────────────────────────────────────────────────────────

function ShareAddressBookModal({ book, org, onClose, onShared }) {
    const [tab, setTab] = useState("devices"); // "devices" | "groups"
    const [devices, setDevices] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [selectedDeviceIds, setSelectedDeviceIds] = useState(new Set());
    const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());
    const [permission, setPermission] = useState("read_only");
    const [saving, setSaving] = useState(false);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [devRes, grpRes, shareRes] = await Promise.all([
                org?.code ? fetchOrganizationDevices(org.code, true) : Promise.resolve({ success: true, data: [] }),
                org?.id ? fetchPolicyGroups(org.id) : Promise.resolve({ success: true, data: [] }),
                getAddressBookShares(book.id),
            ]);

            if (devRes.success) setDevices(Array.isArray(devRes.data) ? devRes.data : []);
            if (grpRes.success) setGroups(Array.isArray(grpRes.data) ? grpRes.data : []);

            // Pre-select whatever the book is already shared with.
            if (shareRes.success && Array.isArray(shareRes.data)) {
                const devIds = new Set();
                const grpIds = new Set();
                shareRes.data.forEach((s) => {
                    if (s.targetType === "device") devIds.add(s.targetRefId);
                    else if (s.targetType === "group") grpIds.add(s.targetRefId);
                });
                setSelectedDeviceIds(devIds);
                setSelectedGroupIds(grpIds);
            }
        } finally {
            setLoading(false);
        }
    }, [org?.code, org?.id, book.id]);

    useEffect(() => { loadAll(); }, [loadAll]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape" && !saving) onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [saving, onClose]);

    const toggle = (setFn) => (id) => {
        setFn((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const toggleDevice = toggle(setSelectedDeviceIds);
    const toggleGroup = toggle(setSelectedGroupIds);

    const list = tab === "devices" ? devices : groups;
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const arr = !q ? list : list.filter((x) => (x.name || "").toLowerCase().includes(q));
        return [...arr].sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
        );
    }, [list, search]);

    const totalSelected = selectedDeviceIds.size + selectedGroupIds.size;

    const handleSave = async () => {
        if (totalSelected === 0) {
            toast.error("Select at least one device or device group.");
            return;
        }
        setSaving(true);
        const res = await shareAddressBook({
            addressBookId: book.id,
            deviceIds: Array.from(selectedDeviceIds),
            policyGroupIds: Array.from(selectedGroupIds),
            permission,
        });
        setSaving(false);
        if (res.success) {
            toast.success("Address book shared");
            if (onShared) onShared();
            onClose();
        } else {
            toast.error(res.error || "Failed to share address book");
        }
    };

    const TabBtn = ({ id, icon, label, count }) => (
        <button
            type="button"
            onClick={() => { setTab(id); setSearch(""); }}
            style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                padding: "10px 16px", fontSize: 14, fontWeight: 600,
                color: tab === id ? "#1976d2" : "#777",
                background: "none", border: "none",
                borderBottom: `2px solid ${tab === id ? "#1976d2" : "transparent"}`,
                cursor: "pointer", marginBottom: -1,
            }}
        >
            {icon}
            {label}
            {count > 0 && (
                <span style={{
                    fontSize: 11, fontWeight: 700, color: "#1976d2",
                    background: "#e8f0fe", borderRadius: 20, padding: "1px 7px",
                }}>{count}</span>
            )}
        </button>
    );

    return (
        <div
            style={{
                position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
                zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center",
            }}
            onClick={() => !saving && onClose()}
        >
            <div
                style={{
                    background: "#fff", borderRadius: 16, width: 580, maxWidth: "94vw",
                    maxHeight: "88vh", display: "flex", flexDirection: "column",
                    boxShadow: "0 24px 64px rgba(0,0,0,0.18)", overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ padding: "24px 28px 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1a1a2e" }}>Share Address Book</h3>
                        <p style={{ margin: "4px 0 0", fontSize: 13, color: "#888" }}>
                            Sharing <strong style={{ color: "#555" }}>{book.name}</strong> with devices or device groups in this organisation.
                        </p>
                    </div>
                    <button onClick={() => !saving && onClose()}
                        style={{ background: "none", border: "none", fontSize: 22, color: "#aaa", cursor: "pointer", lineHeight: 1, padding: 0 }}>×</button>
                </div>

                {/* Tabs */}
                <div style={{ padding: "16px 28px 0", flexShrink: 0, borderBottom: "1px solid #e8eaf0", display: "flex", gap: 4 }}>
                    <TabBtn id="devices" icon={<DevicesOtherOutlinedIcon sx={{ fontSize: "1.15rem" }} />} label="Device(s)" count={selectedDeviceIds.size} />
                    <TabBtn id="groups" icon={<GroupsOutlinedIcon sx={{ fontSize: "1.15rem" }} />} label="Device Group(s)" count={selectedGroupIds.size} />
                </div>

                {/* Search */}
                <div style={{ padding: "14px 28px 10px", flexShrink: 0 }}>
                    <div style={{ position: "relative" }}>
                        <SearchIcon style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#bbb", fontSize: 16, pointerEvents: "none" }} />
                        <input
                            type="text"
                            placeholder={tab === "devices" ? "Search devices…" : "Search device groups…"}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            disabled={loading}
                            style={{
                                width: "100%", paddingLeft: 32, paddingRight: 12,
                                paddingTop: 8, paddingBottom: 8,
                                border: "1px solid #e0e0e0", borderRadius: 8,
                                fontSize: 14, outline: "none", background: "#fafafa",
                                boxSizing: "border-box",
                            }}
                        />
                    </div>
                </div>

                {/* List */}
                <div style={{ flex: 1, overflowY: "auto", padding: "0 28px 8px" }}>
                    {loading ? (
                        <div style={{ textAlign: "center", padding: "48px 0", color: "#aaa" }}>
                            <i className="fa fa-spinner fa-spin" style={{ fontSize: 24, color: "#1976d2" }} />
                            <p style={{ marginTop: 12, fontSize: 14 }}>Loading…</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "44px 20px", color: "#aaa" }}>
                            <p style={{ fontSize: 14 }}>
                                {tab === "devices" ? "No devices found." : "No device groups found."}
                            </p>
                        </div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {tab === "devices"
                                ? filtered.map((d) => (
                                    <SelectRow
                                        key={d.id}
                                        title={d.name}
                                        subtitle={d.serialNo ? `Serial: ${d.serialNo}` : null}
                                        imageSrc={d.profilePicture ? `data:image/png;base64,${d.profilePicture}` : null}
                                        checked={selectedDeviceIds.has(d.id)}
                                        onToggle={() => toggleDevice(d.id)}
                                    />
                                ))
                                : filtered.map((g) => (
                                    <SelectRow
                                        key={g.id}
                                        title={g.name}
                                        subtitle={`${g.noOfDevices ?? 0} device${(g.noOfDevices ?? 0) !== 1 ? "s" : ""}`}
                                        checked={selectedGroupIds.has(g.id)}
                                        onToggle={() => toggleGroup(g.id)}
                                    />
                                ))}
                        </div>
                    )}
                </div>

                {/* Permission + footer */}
                <div style={{ borderTop: "1px solid #eee", padding: "14px 28px 20px", flexShrink: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#444" }}>Permission</span>
                        <select
                            value={permission}
                            onChange={(e) => setPermission(e.target.value)}
                            disabled={saving}
                            style={{
                                padding: "7px 12px", border: "1.5px solid #e0e0e0",
                                borderRadius: 8, fontSize: 13, outline: "none",
                                background: "#fff", cursor: "pointer",
                            }}
                        >
                            <option value="read_only">Read only</option>
                            <option value="editable">Editable</option>
                        </select>
                        <span style={{ marginLeft: "auto", fontSize: 13, color: totalSelected > 0 ? "#1976d2" : "#bbb", fontWeight: totalSelected > 0 ? 600 : 400 }}>
                            {totalSelected} selected
                        </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                        <button type="button" className="btn btn-secondary" onClick={() => !saving && onClose()} disabled={saving}>
                            Cancel
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving || totalSelected === 0}>
                            {saving ? "Sharing…" : "Save Sharing"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ShareAddressBookModal;
